// src/pages/MfaSetup.jsx
import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";

// Cloud Functions HTTP base URL (region: us-central1)
const FN_BASE = "https://us-central1-tdo-emp.cloudfunctions.net";

export default function MfaSetup() {
  const nav = useNavigate();
  const loc = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [code, setCode] = useState("");

  const [otpauthUrl, setOtpauthUrl] = useState("");
  const [secretBase32, setSecretBase32] = useState("");
  const [alreadyEnabled, setAlreadyEnabled] = useState(false);

  const redirectTarget = loc.state?.from?.pathname || "/dashboard";

  // Show QR only when NOT enabled and we have url
  const shouldShowQr = useMemo(
    () => !!otpauthUrl && !alreadyEnabled,
    [otpauthUrl, alreadyEnabled]
  );

  // Load / generate secret from HTTP function
  useEffect(() => {
    const load = async () => {
      setErr("");

      const user = auth.currentUser;
      if (!user) {
        nav("/login", { replace: true });
        return;
      }

      try {
        const token = await user.getIdToken();
        const res = await fetch(`${FN_BASE}/generateAdminTotpSecret`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data.error || "Failed to generate secret");
        }

        // 🔐 If already enabled, backend will NOT send secret or otpauthUrl
        if (data.enabled) {
          setAlreadyEnabled(true);
          setOtpauthUrl("");
          setSecretBase32("");
        } else {
          setAlreadyEnabled(false);
          setOtpauthUrl(data.otpauthUrl || "");
          setSecretBase32(data.secretBase32 || data.base32 || "");
        }
      } catch (e) {
        console.error("generateAdminTotpSecret error", e);
        setErr(e.message || "Unable to generate authenticator. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [nav]);

  // Verify code & enable TOTP
  async function handleVerify(e) {
    e.preventDefault();
    if (!code || saving) return;

    setErr("");
    setSaving(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Session expired. Please login again.");

      const token = await user.getIdToken();
      const res = await fetch(`${FN_BASE}/enableAdminTotp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: code.trim() }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Invalid code");
      }

      setAlreadyEnabled(true);
      nav(redirectTarget, { replace: true });
    } catch (e) {
      console.error("enableAdminTotp error", e);
      setErr(e.message || "Invalid code.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSignOut() {
    try {
      await auth.signOut();
    } finally {
      nav("/login", { replace: true });
    }
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        bgcolor: "#f6f7fb",
        px: 2,
      }}
    >
      <Card sx={{ width: 420, maxWidth: "100%" }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
            Set up Authenticator
          </Typography>

          {alreadyEnabled ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                TOTP authentication is already enabled on your account.
              </Alert>

              <Button
                variant="contained"
                fullWidth
                onClick={() => nav(redirectTarget, { replace: true })}
              >
                Continue to Dashboard
              </Button>
            </>
          ) : (
            <>
              {err && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {err}
                </Alert>
              )}

              <Typography variant="body2" sx={{ mb: 2 }}>
                Scan this QR code using <strong>Google Authenticator</strong>,
                <strong> Microsoft Authenticator</strong>, or any TOTP app.
              </Typography>

              {shouldShowQr && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <QRCodeCanvas value={otpauthUrl} size={240} />
                </Box>
              )}

              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.03)",
                }}
              >
                <Typography variant="caption">
                  Manual Entry (Time-based Key):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", mt: 0.5 }}
                >
                  {secretBase32}
                </Typography>
              </Box>

              <form onSubmit={handleVerify}>
                <TextField
                  label="6-digit code"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />

                <Stack direction="row" spacing={1}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={saving || !code}
                  >
                    {saving ? "Verifying…" : "Verify & Enable"}
                  </Button>

                  {/* No "Skip" – strong security */}
                  <Button
                    fullWidth
                    variant="text"
                    onClick={handleSignOut}
                  >
                    Sign out
                  </Button>
                </Stack>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
